import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  applySpec,
  defaultTo,
  equals,
  head,
  pick,
  pipe,
  prop,
  filter as rFilter
} from 'ramda'

import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { useCompanyListingsData } from '@/lib/company'
import { useFilters } from '@/hooks/use-filters'
import CompanyProfile from '@/features/Company/company-profile'
import { CompanyMetaTags } from '@/features/Company/seo'
import useListingsFavourites from '@/features/Listings/hooks/use-listings-favourites'
import ListingFilter from '@/features/Listings/listing-filter'
import BrokerInfo from '@/components/broker-info'
import { Company } from '@/components/icons'
import SearchTitle from '@/components/search-title'

import FullList from './full-list'

const first = pipe(defaultTo([]), head, defaultTo({}))

const brokerInfo = pipe(
  first,
  pick(['broker_files', 'broker']),
  applySpec({
    name: ({ broker }) => broker?.name,
    logo: ({ broker_files }) =>
      pipe(
        defaultTo([]),
        rFilter(pipe(prop('tag'), equals('broker_logo'))),
        first,
        prop('location'),
        first,
        prop('key')
      )(broker_files)
  })
)
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`

const ResultsHeaderWrapper = styled.div`
  max-width: ${breakpoints.desktop};
  display: flex;
  flex-direction: column;
  position: relative;
  width: calc(100% - 40px);
  margin: 20px 20px 0;
  padding-bottom: 20px;

  &:after {
    content: '';
    position: absolute;
    background: ${colors.grey88};
    height: 1px;
    width: 100%;
    bottom: 0px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding-bottom: 0;
    &:after {
      display: none;
    }
  }
`

const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 0;
  }
`

const ListsWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
`

const ListingSelectionWrapper = styled.div`
  display: flex;
  width: 225px;

  p:first-child {
    border-right: 1px solid ${colors.regular};
    text-align: right;
  }

  @media screen and (max-width: 700px) {
    width: 265px;
  }
`

const ListingSelectText = styled.p`
  width: 125px;
  padding: 0 15px;
  font-size: ${fontSizes.medium};
  line-height: 30px;
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.49px;
  cursor: pointer;
  color: ${props => (props.active ? `${colors.primary}` : `${colors.regular}`)};

  @media screen and (max-width: 700px) {
    width: 145px;
    padding: 0 25px;
  }
`

const CompanyNameWrapper = styled.div`
  display: flex;
  margin: 40px auto 24px;
  align-items: center;
  justify-content: center;
  max-width: 1096px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    padding-left: 20px;
    padding-right: 20px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 30px auto;
    padding-bottom: 30px;
  }
`

const CompanyIcon = styled(Company)`
  height: 70px;
  width: 47px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 48px;
    width: 34px;
  }
`

const CompanyName = styled.h1`
  font-size: 48px;
  font-weight: normal;
  text-align: center;
  margin: 0 0 0 30px;
  line-height: 65px;
  font-weight: ${fontWeights.medium};
  text-align: left;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 36px;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: unset;
    font-size: 24px;
    line-height: unset;
    margin-left: 10px;
  }
`

const ListingList = ({
  companyData,
  companyId,
  companyProfile,
  isGenericPage,
  isCustomPage,
  isVipPage,
  listingsRef,
  footerOnScreen,
  listingsOnScreen
}) => {
  useListingsFavourites()
  const { t } = useTranslation()
  const [, setFilter] = useFilters()
  const companyName = companyProfile?.name || companyData.company?.name
  const agentName = companyData?.agents?.[0]?.name
    .split(' ')
    .slice(0, -1)
    .join(' ')
  const pageTitle =
    companyName ||
    `${/s$/i.test(agentName) ? `${agentName}'` : `${agentName}'s`} Listings`
  const hasTitle = companyName || agentName
  const SeoComponent = companyProfile?.seoComponent || null

  const { data, errors, loading, preload, filters } = useCompanyListingsData(
    companyId,
    isVipPage
  )

  const filter = filterParams => {
    setFilter({ ...filterParams, page: null })
  }

  const { featured = {}, companyListings: { feed = {}, metadata = {} } = {} } =
    data.companyListSearch || {}

  const broker = brokerInfo(feed)

  if (errors) console.log(errors)

  const stateMachine = filters.state_machine || 'published'

  return (
    <>
      {(isGenericPage || isCustomPage) && (
        <CompanyMetaTags
          companyData={isCustomPage ? companyProfile : companyData}
          companyId={companyProfile?.url || companyId}
          listings={feed}
          companyCity={companyData.company?.city}
          companyName={companyName}
          agentName={companyData.agents?.[0]?.name || ''}
          isLoading={loading}
          metadata={metadata}
        />
      )}
      {isGenericPage && (
        <BrokerInfo
          id="broker-info"
          broker={broker}
          margins
          bold
          maxWidth={160}
          maxHeight={120}
        />
      )}
      {isVipPage ? (
        <CompanyProfile
          data={companyData}
          footerOnScreen={footerOnScreen}
          listingsOnScreen={listingsOnScreen}
          metadata={metadata}
        />
      ) : (
        <CompanyNameWrapper>
          {hasTitle && (
            <>
              <CompanyIcon />
              <CompanyName>
                {isCustomPage ? companyProfile.name : pageTitle}
              </CompanyName>
            </>
          )}
        </CompanyNameWrapper>
      )}
      <div ref={listingsRef}>
        <Wrapper>
          {(isCustomPage || isVipPage) && (
            <ListingSelectionWrapper>
              <ListingSelectText
                onClick={() => filter({ state_machine: null })}
                active={stateMachine === 'published'}
              >
                {t('b.listing.view.available.text', 'Available')}
              </ListingSelectText>
              <ListingSelectText
                onClick={() => filter({ state_machine: 'rented' })}
                active={stateMachine === 'rented'}
              >
                {t('common.rented', 'Rented')}
              </ListingSelectText>
            </ListingSelectionWrapper>
          )}
          <ResultsHeaderWrapper>
            <SearchTitle
              listingCount={metadata.total_count}
              hideHeader
              loading={loading}
            />
            <FiltersWrapper>
              <ListingFilter companyId={companyId} hideAlertButton />
            </FiltersWrapper>
          </ResultsHeaderWrapper>
          <ListsWrapper>
            <FullList
              setFilter={setFilter}
              listings={feed}
              featuredListings={isGenericPage ? featured : []}
              metadata={metadata}
              preload={preload}
              isLoading={loading}
              companyName={companyName || agentName}
              isRented={stateMachine === 'rented'}
            />
          </ListsWrapper>
        </Wrapper>
      </div>
      {SeoComponent && <SeoComponent data={companyProfile} />}
    </>
  )
}

export default ListingList
